import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "react-toastify";
import { ArrowRight, Check, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Button } from "~/atoms/button";
import { DocumentConfetti } from "~/molecules/DocumentConfetti";
import { OnboardFooter } from "~/molecules/onboard-documents/OnboardFooter";
import { CompleteTable } from "~/molecules/onboard-documents/CompleteTable";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { updateDocument, updateDocumentVersion } from "~/api/v2/requests";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { StickyHorizontalScrollbar } from "~/molecules/StickyHorizontalScrollbar";
import { DocumentVersionStatus } from "~/config/types";
import {
  type DocumentVersionSummary,
  type ScheduleReviewPeriod,
} from "~/api/types";
import { removeFromSet } from "~/helpers/set";

interface CompleteStepProps {
  createdVersionIds: number[];
  isCompleted: boolean;
  onComplete: () => void;
  onRestart: () => void;
}

export const CompleteStep = ({
  createdVersionIds,
  isCompleted,
  onComplete,
  onRestart,
}: CompleteStepProps) => {
  const { t } = useTranslation();
  const { data: allVersions, mutate } = useDocumentVersions();

  const versions = useMemo<DocumentVersionSummary[]>(() => {
    if (!allVersions) return [];
    const createdSet = new Set(createdVersionIds);
    return allVersions.filter((v) => createdSet.has(v.id));
  }, [allVersions, createdVersionIds]);

  const orderedRows = useMemo<DocumentVersionSummary[]>(
    () => [...versions].sort((a, b) => a.title.localeCompare(b.title)),
    [versions],
  );

  const { selectedIds, allSelected, toggleRow, toggleAll, bulkTargets } =
    useBulkSelection(orderedRows.map((r) => r.id));

  const [statusOverrides, setStatusOverrides] = useState<
    Map<number, DocumentVersionStatus>
  >(() => new Map());
  const [reviewPeriodOverrides, setReviewPeriodOverrides] = useState<
    Map<number, ScheduleReviewPeriod>
  >(() => new Map());
  const [publishingIds, setPublishingIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [isCompleting, setIsCompleting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const statusById = useMemo(() => {
    const map = new Map<number, DocumentVersionStatus>();
    for (const row of versions) {
      map.set(
        row.id,
        statusOverrides.get(row.id) ?? DocumentVersionStatus.published,
      );
    }
    return map;
  }, [versions, statusOverrides]);

  const reviewPeriodById = useMemo(() => {
    const map = new Map<number, ScheduleReviewPeriod>();
    for (const row of versions) {
      map.set(
        row.id,
        reviewPeriodOverrides.has(row.id)
          ? reviewPeriodOverrides.get(row.id)!
          : row.document.scheduleReviewPeriod,
      );
    }
    return map;
  }, [versions, reviewPeriodOverrides]);

  const setStatus = (
    id: number,
    status: DocumentVersionStatus,
    opts?: { bulk?: boolean },
  ) => {
    const targets = opts?.bulk ? bulkTargets(id) : new Set([id]);
    setStatusOverrides((prev) => {
      const next = new Map(prev);
      for (const tid of targets) next.set(tid, status);
      return next;
    });
  };

  const setReviewPeriod = (
    id: number,
    period: ScheduleReviewPeriod,
    opts?: { bulk?: boolean },
  ) => {
    const targets = opts?.bulk ? bulkTargets(id) : new Set([id]);
    setReviewPeriodOverrides((prev) => {
      const next = new Map(prev);
      for (const tid of targets) next.set(tid, period);
      return next;
    });
  };

  const getReviewPeriodForRow = (row: DocumentVersionSummary) =>
    reviewPeriodById.get(row.id) ?? null;

  const saveReviewPeriod = async (
    row: DocumentVersionSummary,
  ): Promise<boolean> => {
    const scheduleReviewPeriod = getReviewPeriodForRow(row);
    if (scheduleReviewPeriod === row.document.scheduleReviewPeriod) {
      return true;
    }

    try {
      await updateDocument(row.document.id, { scheduleReviewPeriod });
      await mutate(
        (current) =>
          current?.map((v) =>
            v.id === row.id
              ? {
                  ...v,
                  document: {
                    ...v.document,
                    scheduleReviewPeriod,
                  },
                }
              : v,
          ),
        { revalidate: false },
      );
      return true;
    } catch {
      return false;
    }
  };

  const publishRow = async (id: number): Promise<boolean> => {
    try {
      await updateDocumentVersion(id, {
        status: DocumentVersionStatus.published,
      });
      await mutate(
        (current) =>
          current?.map((v) =>
            v.id === id ? { ...v, status: DocumentVersionStatus.published } : v,
          ),
        { revalidate: false },
      );
      return true;
    } catch {
      return false;
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const reviewPeriodUpdates = orderedRows.filter(
        (row) =>
          getReviewPeriodForRow(row) !== row.document.scheduleReviewPeriod,
      );
      const toPublish = orderedRows.filter(
        (r) =>
          r.status === DocumentVersionStatus.draft &&
          r.permissions.canPublish &&
          statusById.get(r.id) === DocumentVersionStatus.published,
      );

      let updatedReviewPeriodCount = 0;
      const failedReviewPeriodIds: number[] = [];
      for (const row of reviewPeriodUpdates) {
        if (await saveReviewPeriod(row)) updatedReviewPeriodCount++;
        else failedReviewPeriodIds.push(row.id);
      }

      if (failedReviewPeriodIds.length > 0) {
        void mutate();
        toast.error(
          t(
            ($) =>
              $.library.onboardDocuments.steps.configureAndPublish.toasts
                .failedRecurringReview,
            { count: failedReviewPeriodIds.length },
          ),
        );
        return;
      }

      if (updatedReviewPeriodCount > 0) {
        toast.success(
          t(
            ($) =>
              $.library.onboardDocuments.steps.configureAndPublish.toasts
                .recurringReviewUpdated,
            { count: updatedReviewPeriodCount },
          ),
        );
      }

      if (toPublish.length === 0) {
        setShowConfetti(true);
        onComplete();
        return;
      }

      setPublishingIds(new Set(toPublish.map((r) => r.id)));

      let succeededCount = 0;
      const failedIds: number[] = [];
      for (const row of toPublish) {
        if (await publishRow(row.id)) succeededCount++;
        else failedIds.push(row.id);
        setPublishingIds((prev) => removeFromSet(prev, row.id));
      }

      if (failedIds.length > 0) void mutate();

      if (succeededCount > 0) {
        toast.success(
          t(
            ($) =>
              $.library.onboardDocuments.steps.configureAndPublish.toasts
                .published,
            { count: succeededCount },
          ),
        );
      }
      if (failedIds.length > 0) {
        toast.error(
          t(
            ($) =>
              $.library.onboardDocuments.steps.configureAndPublish.toasts
                .failedPublish,
            { count: failedIds.length },
          ),
        );
        return;
      }

      setShowConfetti(true);
      onComplete();
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish.cardTitle,
            )}
          </CardTitle>
          <CardDescription>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish
                  .cardDescription,
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CompleteTable
            readOnly={isCompleted || isCompleting}
            wrapperRef={tableWrapperRef}
            rows={orderedRows}
            selectedIds={selectedIds}
            allSelected={allSelected}
            publishingIds={publishingIds}
            statusById={statusById}
            reviewPeriodById={reviewPeriodById}
            onToggleAll={toggleAll}
            onToggleRow={toggleRow}
            onStatusChange={setStatus}
            onReviewPeriodChange={setReviewPeriod}
          />
        </CardContent>
      </Card>
      <StickyHorizontalScrollbar
        targetRef={tableWrapperRef}
        deps={[orderedRows, isCompleted]}
        className="sticky bottom-12 h-4"
      />
      <OnboardFooter
        right={
          isCompleted ? (
            <>
              <Button variant="outline" onClick={onRestart}>
                <RefreshCw className="size-4" />
                {t(
                  ($) =>
                    $.library.onboardDocuments.steps.configureAndPublish
                      .onboardMore,
                )}
              </Button>
              <Button asChild>
                <Link to="/documents?status=published&status=draft">
                  {t(
                    ($) =>
                      $.library.onboardDocuments.steps.configureAndPublish
                        .goToLibrary,
                  )}
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </>
          ) : (
            <Button onClick={handleComplete} disabled={isCompleting}>
              <Check />
              {t(
                ($) =>
                  $.library.onboardDocuments.steps.configureAndPublish
                    .completeOnboarding,
              )}
            </Button>
          )
        }
      />
      <DocumentConfetti
        show={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />
    </div>
  );
};
